"use client";

import { fetchAuthSession } from "aws-amplify/auth";
import type { ServerCart, ServerCartItem } from "./server-types";
import type { Variety } from "@/lib/catalog/types";
import {
  hasClientAdjustments,
  recordCartAdjustments,
  type ClientCartAdjustments,
} from "./price-update-notice";

export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    return token ?? null;
  } catch {
    return null;
  }
}

// #532 cart-save: EVERY browser lookup of /api/catalog/* goes through here so
// it carries the fresh access token (getAccessToken refreshes an expired one
// first). Without it the server judged price access from the mag-portal-access
// cookie alone, which lags the token refresh: a tab waking from idle got
// price-stripped records, the cart rebuilt its lines at $0 and the save was
// refused ("We couldn't save your cart"). Signed-out: no token, same request.
export async function catalogFetch(url: string): Promise<Response> {
  const token = await getAccessToken();
  return fetch(url, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
}

async function authedFetch(
  url: string,
  init: RequestInit & { token: string },
): Promise<Response> {
  const { token, headers, ...rest } = init;
  return fetch(url, {
    ...rest,
    headers: {
      ...(headers ?? {}),
      Authorization: `Bearer ${token}`,
    },
  });
}

// PASTE #570: the server may return `adjustments` with the cart (a stale line
// price corrected to the catalog, a sold-out line dropped). They are recorded
// for the in-cart notices here and handed back on the cart so the sync
// boundary can adopt the corrected lines locally.
export type ServerCartWithAdjustments = ServerCart & { adjustments?: ClientCartAdjustments };

export async function fetchServerCart(
  token: string,
): Promise<ServerCartWithAdjustments | null> {
  const r = await authedFetch("/api/cart", { token, method: "GET" });
  if (!r.ok) return null;
  const body = (await r.json()) as { cart?: ServerCart; adjustments?: ClientCartAdjustments };
  if (!body.cart) return null;
  if (hasClientAdjustments(body.adjustments)) {
    recordCartAdjustments(body.adjustments);
    return { ...body.cart, adjustments: body.adjustments };
  }
  return body.cart;
}

export type PutCartPayload = {
  items: ServerCartItem[];
  deliveryDate?: string | null;
};

export async function putServerCart(
  token: string,
  payload: PutCartPayload,
): Promise<ServerCartWithAdjustments | null> {
  const r = await authedFetch("/api/cart", {
    token,
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      cart: {
        items: payload.items,
        deliveryDate: payload.deliveryDate ?? null,
        packing: null,
        rolloverHistory: [],
        deliveryDateAutoRolled: false,
        paymentMethod: null,
        status: "draft",
      },
    }),
  });
  if (!r.ok) {
    // #525: a REJECTED save (the server refused the cart, e.g. a price
    // mismatch) used to fail silently, leaving the client on a cart that was
    // never stored -> broken/blank checkout. Announce it so the cart + checkout
    // show a clear message. Transient errors (5xx/network) are not announced.
    if (r.status >= 400 && r.status < 500 && typeof window !== "undefined") {
      let reason = "";
      try {
        const b = (await r.json()) as { reason?: string; error?: string };
        reason = b.reason ?? b.error ?? "";
      } catch {
        reason = "";
      }
      window.dispatchEvent(new CustomEvent(CART_SAVE_REJECTED_EVENT, { detail: { status: r.status, reason } }));
    }
    return null;
  }
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(CART_SAVE_OK_EVENT));
  const body = (await r.json()) as { cart?: ServerCart; adjustments?: ClientCartAdjustments };
  if (!body.cart) return null;
  if (hasClientAdjustments(body.adjustments)) {
    recordCartAdjustments(body.adjustments);
    return { ...body.cart, adjustments: body.adjustments };
  }
  return body.cart;
}

// #525: browser events for cart-save rejection / recovery (see CartSaveRejectedBanner).
export const CART_SAVE_REJECTED_EVENT = "mag-portal-cart-save-rejected";
export const CART_SAVE_OK_EVENT = "mag-portal-cart-save-ok";

export async function deleteServerCart(token: string): Promise<boolean> {
  const r = await authedFetch("/api/cart", { token, method: "DELETE" });
  return r.ok;
}

export async function fetchVarietiesBatch(
  slugs: string[],
): Promise<Map<string, Variety>> {
  const map = new Map<string, Variety>();
  if (slugs.length === 0) return map;
  const unique = Array.from(new Set(slugs));
  const r = await catalogFetch(
    `/api/catalog/by-slugs?slugs=${encodeURIComponent(unique.join(","))}`,
  );
  if (!r.ok) return map;
  const body = (await r.json()) as { varieties: Variety[] };
  for (const v of body.varieties ?? []) {
    map.set(v.slug, v);
  }
  return map;
}
